import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

/**
 * Who may launch a scenario into a class.
 *
 * This replaces `?scenarioLaunch=1` — a URL parameter kept in the browser's own
 * storage, which no administrator could grant, audit or take away, and which in
 * practice meant the launch button was reachable by nobody in the field while
 * the entire scenario pipeline sat behind it.
 *
 * The switch now lives in the database (`pm.scenario_builder`
 * ScenarioLaunchAccess): a row is an explicit decision by a named admin, and
 * with no row the answer is derived — a trainer who runs a class with trainees
 * in it can launch, because those are the people the feature is for.
 *
 * TRANSITIONAL: until these routes are registered, `fetchMyLaunchAccess`
 * answers `unavailable` rather than throwing, and the caller falls back to the
 * old parameter. That keeps the pilot working and makes the switchover a
 * backend deploy, not a coordinated release.
 */

export type LaunchAccessSource =
  | 'admin'
  | 'granted'
  | 'revoked'
  | 'class_default'
  | 'no_class'
  | 'not_a_trainer';

export type LaunchAccessRecord = {
  granted_at?: string | null;
  granted_by?: string | null;
  revoked_at?: string | null;
  revoked_by?: string | null;
  note?: string;
};

export type LaunchAccessAnswer = {
  user_id?: number;
  username?: string;
  allowed: boolean;
  source: LaunchAccessSource | string;
  explanation: string;
  record?: LaunchAccessRecord | null;
};

/** `undefined` = the server could not answer (route absent, or offline). */
export type MaybeLaunchAccess = LaunchAccessAnswer | undefined;

const ADMIN_PATH = '/scenario-builder/admin/launch-access';
const MINE_PATH = '/scenario-builder/launch-access/mine';

const call = async <Payload extends object>(
  path: string,
  method: 'GET' | 'POST',
  data?: Payload,
) => {
  const response = await apiCaller<Payload>({
    path,
    method,
    data,
    apiUrl: API_URL,
  });
  return response;
};

/**
 * The signed-in user's own answer. Never throws: a trainer whose dashboard
 * cannot reach this route must still get a dashboard.
 */
export const fetchMyLaunchAccess = async (): Promise<MaybeLaunchAccess> => {
  try {
    const response = await call(MINE_PATH, 'GET', {});
    if (response.status < 200 || response.status >= 300) return undefined;
    const result = response.result as Partial<LaunchAccessAnswer> | undefined;
    if (!result || typeof result.allowed !== 'boolean') return undefined;
    return {
      allowed: result.allowed,
      source: result.source || '',
      explanation: result.explanation || '',
    };
  } catch {
    return undefined;
  }
};

/** Every trainer's answer, for the admin screen. Throws on a real failure so
 *  the screen can say what went wrong; answers `undefined` when the route is
 *  simply not there yet, and the column then does not render at all. */
export const fetchLaunchAccessRoster = async (): Promise<
  LaunchAccessAnswer[] | undefined
> => {
  let response;
  try {
    response = await call(ADMIN_PATH, 'GET', {});
  } catch {
    return undefined;
  }
  if (response.status === 404 || response.status === 405) return undefined;
  if (response.status < 200 || response.status >= 300) {
    throw new Error('Scenario launch access could not be read.');
  }
  const body = response.result as
    | { rows?: LaunchAccessAnswer[] }
    | LaunchAccessAnswer[]
    | undefined;
  if (Array.isArray(body)) return body;
  return body?.rows || [];
};

/** Grant or withdraw one trainer's access. The note is optional on purpose:
 *  forcing a sentence on a routine pilot grant teaches people to type "x". */
export const setLaunchAccess = async (
  userId: number,
  allowed: boolean,
  note = '',
): Promise<LaunchAccessAnswer> => {
  const response = await call(ADMIN_PATH, 'POST', {
    user_id: userId,
    allowed,
    note,
  });
  if (response.status < 200 || response.status >= 300) {
    throw new Error(
      allowed
        ? 'Access could not be granted.'
        : 'Access could not be withdrawn.',
    );
  }
  return response.result as LaunchAccessAnswer;
};
